import { useState } from 'react';
import type {
  AiLeadClassification,
  AiLeadQualificationResponse,
  AiLeadPriority,
  AiQualificationCountsResponse,
  AiQualificationListType,
  AiQualificationMatchType,
  AiReviewOutcome,
  ListAiLeadQualificationsQuery,
} from '@orion/contracts';
import { Button, Checkbox, ConfirmDialog, Dialog, EmptyState, Pill } from '@orion/ui';
import { ApiError } from '../../lib/api-client.js';
import { useToast } from '../../lib/toast.js';
import { PageHeader } from '../../app/shell.js';
import { ListPagination } from '../../components/ListPagination.js';
import {
  useAiLeadQualificationQuery,
  useAiLeadQualificationsQuery,
  useAiQualificationCountsQuery,
  useAiQualificationEmailRulesQuery,
  useCreateAiQualificationEmailRule,
  useDeleteAiLeadQualification,
  useDeleteAiQualificationEmailRule,
  useReviewAiLeadQualification,
} from './api.js';

type Tab = 'needs_review' | 'rejected' | 'error' | 'junk' | 'all' | 'rules';

const TABS: Array<{ key: Tab; label: string; query?: Partial<ListAiLeadQualificationsQuery> }> = [
  { key: 'needs_review', label: 'Needs review', query: { decision: 'needs_review', reviewed: 'pending' } },
  { key: 'rejected', label: 'Not a lead', query: { decision: 'rejected' } },
  { key: 'error', label: 'Failed', query: { decision: 'error' } },
  { key: 'junk', label: 'Junk', query: { reviewOutcome: 'marked_spam' } },
  { key: 'all', label: 'All history', query: {} },
  { key: 'rules', label: 'Rules' },
];

const CLASSIFICATION_COLOR: Record<AiLeadClassification, string> = {
  SALES_LEAD: '#1F7A54',
  EXISTING_CUSTOMER: '#2E6FD6',
  SUPPORT: '#6B5FD6',
  PARTNER: '#C7781A',
  VENDOR: '#9A9FA8',
  NEWSLETTER: '#9A9FA8',
  MARKETING: '#9A9FA8',
  SPAM: '#B42318',
  INTERNAL: '#5E6470',
  AUTOMATED: '#5E6470',
  OTHER: '#5E6470',
};

function ClassificationPill({ classification }: { classification: AiLeadClassification | null }) {
  if (!classification) return <span className="text-text-faint">—</span>;
  return <Pill label={classification.replaceAll('_', ' ')} color={CLASSIFICATION_COLOR[classification]} />;
}

function PriorityBadge({ priority }: { priority: AiLeadPriority | null }) {
  if (!priority || priority === 'NONE') return <span className="text-text-faint">—</span>;
  if (priority === 'HOT') return <span className="inline-flex h-5.5 items-center rounded-[6px] bg-amber-soft px-2 text-xs font-semibold text-amber-text">Hot</span>;
  if (priority === 'WARM') return <span className="inline-flex h-5.5 items-center rounded-[6px] bg-primary-soft px-2 text-xs font-semibold text-primary-text">Warm</span>;
  return <span className="inline-flex h-5.5 items-center rounded-[6px] bg-divider px-2 text-xs font-semibold text-text-secondary">Cold</span>;
}

const DECISION_LABEL: Record<AiLeadQualificationResponse['decision'], { label: string; className: string }> = {
  auto_create: { label: 'Auto-created', className: 'bg-success-soft text-success' },
  needs_review: { label: 'Needs review', className: 'bg-amber-soft text-amber-text' },
  rejected: { label: 'Not a lead', className: 'bg-surface-subtle text-text-muted' },
  error: { label: 'Failed', className: 'bg-danger-soft text-danger' },
};

const STAT_TILES: Array<{ key: keyof AiQualificationCountsResponse['today'] | 'pendingNeedsReview' | 'pendingFailed'; label: string; dotClassName: string }> = [
  { key: 'pendingNeedsReview', label: 'Needs review', dotClassName: 'bg-amber' },
  { key: 'pendingFailed', label: 'Failed, unresolved', dotClassName: 'bg-danger' },
  { key: 'autoCreated', label: 'Auto-created today', dotClassName: 'bg-success' },
  { key: 'rejected', label: 'Not a lead today', dotClassName: 'bg-text-faint' },
];

/** A small dashboard-style header: how much is currently waiting on a
 * human (all-time backlog) plus today's volume breakdown — the same
 * "KPI tiles + proportional bar" shape the main Dashboard uses for
 * pipeline-by-stage, scaled down for this one screen. */
function AiQualificationStatsRow() {
  const { data } = useAiQualificationCountsQuery();
  if (!data) return null;

  const values: Record<string, number> = {
    pendingNeedsReview: data.pendingNeedsReview,
    pendingFailed: data.pendingFailed,
    autoCreated: data.today.autoCreated,
    needsReview: data.today.needsReview,
    rejected: data.today.rejected,
    failed: data.today.failed,
  };
  const todayTotal = data.today.autoCreated + data.today.needsReview + data.today.rejected + data.today.failed;
  const barSegments: Array<{ key: string; count: number; className: string; label: string }> = [
    { key: 'autoCreated', count: data.today.autoCreated, className: 'bg-success', label: 'Auto-created' },
    { key: 'needsReview', count: data.today.needsReview, className: 'bg-amber', label: 'Needs review' },
    { key: 'rejected', count: data.today.rejected, className: 'bg-text-faint', label: 'Not a lead' },
    { key: 'failed', count: data.today.failed, className: 'bg-danger', label: 'Failed' },
  ].filter((s) => s.count > 0);

  return (
    <div className="flex flex-col gap-3 rounded-card border border-border bg-surface p-4 shadow-card">
      <div className="grid grid-cols-4 gap-3">
        {STAT_TILES.map((tile) => (
          <div key={tile.key} className="flex flex-col gap-1">
            <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted">
              <span className={`size-1.5 rounded-full ${tile.dotClassName}`} />
              {tile.label}
            </span>
            <span className="text-xl font-[650] text-ink">{values[tile.key]}</span>
          </div>
        ))}
      </div>
      {todayTotal > 0 && (
        <div className="flex flex-col gap-1.5">
          <div className="flex h-2 overflow-hidden rounded-full bg-divider">
            {barSegments.map((s) => (
              <div key={s.key} className={s.className} style={{ width: `${(s.count / todayTotal) * 100}%` }} title={`${s.label}: ${s.count}`} />
            ))}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-text-muted">
            {barSegments.map((s) => (
              <span key={s.key} className="flex items-center gap-1">
                <span className={`size-1.5 rounded-full ${s.className}`} />
                {s.label} · {s.count}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/**
 * The "AI Lead Review" queue — the human-in-the-loop half of AI lead
 * qualification (docs §6). A row here is one `AiLeadQualification` audit
 * record; `AiQualificationService` writes them, this screen is where a
 * reviewer acts on the ones it couldn't auto-decide (and where anyone can
 * look back over the full history for audit purposes). The "Junk" tab is
 * just another filter on the same rows (`reviewOutcome: 'marked_spam'`);
 * "Rules" swaps to a completely different panel (not a qualification-row
 * list at all) for managing the email bypass list.
 */
export function AiLeadReviewPage() {
  const [tab, setTab] = useState<Tab>('needs_review');
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const activeTab = TABS.find((t) => t.key === tab)!;
  const isRulesTab = tab === 'rules';
  const { data, isLoading } = useAiLeadQualificationsQuery({ ...activeTab.query, page, pageSize }, { enabled: !isRulesTab });
  const rows = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const review = useReviewAiLeadQualification();
  const deleteRow = useDeleteAiLeadQualification();
  const toast = useToast();

  const changeTab = (key: Tab) => {
    setTab(key);
    setPage(1);
    setCheckedIds(new Set());
  };

  const toggleRow = (id: string) =>
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAllOnPage = () =>
    setCheckedIds((prev) => (rows.length > 0 && rows.every((r) => prev.has(r.id)) ? new Set() : new Set(rows.map((r) => r.id))));

  const runBulk = async (action: (id: string) => Promise<unknown>, label: string) => {
    const ids = [...checkedIds];
    const results = await Promise.allSettled(ids.map((id) => action(id)));
    const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
    setCheckedIds(new Set());
    if (failures.length > 0) {
      const reason = failures[0]!.reason;
      const detail = reason instanceof ApiError ? (reason.problem.detail ?? reason.problem.title) : 'an unexpected error';
      toast.error(`${label}: ${ids.length - failures.length} succeeded, ${failures.length} failed — ${detail}`);
    } else {
      toast.success(`${label}: ${ids.length} updated.`);
    }
  };
  const bulkDecide = (outcome: AiReviewOutcome, label: string) => () => runBulk((id) => review.mutateAsync({ id, input: { outcome } }), label);
  const bulkDelete = async () => {
    setConfirmBulkDelete(false);
    await runBulk((id) => deleteRow.mutateAsync(id), 'Deleted');
    if (selectedId && checkedIds.has(selectedId)) setSelectedId(undefined);
  };

  return (
    <>
      <PageHeader breadcrumb={[{ label: 'AI Lead Review' }]} />
      <main className="flex flex-1 flex-col gap-3 overflow-hidden px-7 pb-6 pt-5">
        <AiQualificationStatsRow />
        <div role="tablist" aria-label="AI lead review views" className="flex h-10 shrink-0 items-stretch gap-5.5 border-b border-border">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => changeTab(t.key)}
              className={
                tab === t.key
                  ? 'flex items-center gap-1.5 text-sm font-semibold text-ink shadow-[inset_0_-2px_0_var(--color-primary)]'
                  : 'flex items-center gap-1.5 text-sm font-medium text-text-muted hover:text-ink'
              }
            >
              {t.label}
              {t.key === 'needs_review' && tab === 'needs_review' && total > 0 && (
                <span className="rounded-full bg-amber-soft px-1.75 py-px text-xs font-semibold text-amber-text">{total}</span>
              )}
            </button>
          ))}
        </div>

        {isRulesTab ? (
          <EmailRulesPanel />
        ) : (
          <div className="grid min-h-0 flex-1 items-start grid-cols-[1fr_380px] gap-4 overflow-hidden">
            <div className="flex min-h-0 flex-col gap-3">
              <p className="text-xs text-text-muted">
                {tab === 'junk'
                  ? 'Items a reviewer marked as spam. Move one back if that was a mistake, or delete it permanently — deleting only removes this audit row, not any Lead/Contact already created.'
                  : "This queue only holds emails the AI wasn't confident enough to decide on its own. Emails it was confident about already became Leads automatically — no review needed there; check the regular Leads list for those."}
              </p>

              {!isLoading && rows.length === 0 && <EmptyState title="Nothing here" description="No items match this view." />}

              {rows.length > 0 && (
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-card border border-border bg-surface shadow-card">
                  <div className="flex h-10 shrink-0 items-center gap-2 border-b border-divider bg-ground px-4">
                    <Checkbox
                      aria-label="Select all on this page"
                      checked={rows.length > 0 && rows.every((r) => checkedIds.has(r.id))}
                      onChange={toggleAllOnPage}
                      hideLabel
                      label="Select all"
                    />
                    {checkedIds.size > 0 ? (
                      <>
                        <span className="mr-1 text-[13px] font-semibold text-primary-text">{checkedIds.size} selected</span>
                        <Button size="sm" variant="secondary" loading={review.isPending} onClick={bulkDecide('approved_lead', 'Approved')}>
                          Approve as lead
                        </Button>
                        <Button size="sm" variant="secondary" loading={review.isPending} onClick={bulkDecide('rejected', 'Rejected')}>
                          Reject
                        </Button>
                        <Button size="sm" variant="secondary" loading={review.isPending} onClick={bulkDecide('marked_spam', 'Marked as spam')}>
                          Mark as spam
                        </Button>
                        <Button size="sm" variant="danger" onClick={() => setConfirmBulkDelete(true)}>
                          Delete permanently
                        </Button>
                        <div className="flex-1" />
                        <button type="button" aria-label="Clear selection" onClick={() => setCheckedIds(new Set())} className="text-primary-text hover:opacity-70">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                            <path d="M6 6l12 12M18 6 6 18" />
                          </svg>
                        </button>
                      </>
                    ) : (
                      <span className="text-xs text-text-muted">Select rows to approve, reject, spam, or delete in bulk.</span>
                    )}
                  </div>

                  <div className="min-h-0 flex-1 overflow-y-auto">
                    {rows.map((row) => (
                      <div
                        key={row.id}
                        onClick={() => setSelectedId(row.id)}
                        className={`grid h-11 cursor-pointer items-center gap-2 border-b border-divider px-4 text-[13px] last:border-0 ${selectedId === row.id ? 'bg-primary-soft/40' : 'hover:bg-surface-subtle'}`}
                        style={{ gridTemplateColumns: '20px minmax(0,1fr) 140px 90px 70px 90px 70px' }}
                      >
                        <span onClick={(e) => e.stopPropagation()} className="flex justify-center">
                          <Checkbox
                            aria-label={`Select ${row.senderEmail ?? 'row'}`}
                            checked={checkedIds.has(row.id)}
                            onChange={() => toggleRow(row.id)}
                            hideLabel
                            label="Select"
                          />
                        </span>
                        <span className="truncate">
                          <span className="font-medium text-ink">{row.extractedContactName ?? row.senderName ?? row.senderEmail ?? 'Unknown sender'}</span>
                          {row.subject && <span className="text-text-muted"> · {row.subject}</span>}
                        </span>
                        <ClassificationPill classification={row.classification} />
                        <PriorityBadge priority={row.priority} />
                        <span className="text-text-secondary">{row.confidence !== null ? `${Math.round(row.confidence * 100)}%` : '—'}</span>
                        <span className={`inline-flex h-5.5 w-fit items-center rounded-[6px] px-2 text-xs font-semibold ${DECISION_LABEL[row.decision].className}`}>
                          {DECISION_LABEL[row.decision].label}
                        </span>
                        <span className="text-xs text-text-muted">{timeAgo(row.createdAt)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {total > 0 && (
                <ListPagination page={page} pageSize={pageSize} total={total} noun="items" onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} />
              )}
            </div>

            <ReviewDetailPanel id={selectedId} onClose={() => setSelectedId(undefined)} />
          </div>
        )}
      </main>

      <ConfirmDialog
        open={confirmBulkDelete}
        title={`Delete ${checkedIds.size} item${checkedIds.size === 1 ? '' : 's'} permanently?`}
        description="This removes the audit rows for good. Any Lead or Contact already created from them is not affected."
        confirmLabel="Delete"
        variant="danger"
        loading={deleteRow.isPending}
        onConfirm={bulkDelete}
        onClose={() => setConfirmBulkDelete(false)}
      />
    </>
  );
}

const RECLASSIFY_OPTIONS: AiLeadClassification[] = [
  'SALES_LEAD',
  'EXISTING_CUSTOMER',
  'SUPPORT',
  'PARTNER',
  'VENDOR',
  'NEWSLETTER',
  'MARKETING',
  'SPAM',
  'INTERNAL',
  'AUTOMATED',
  'OTHER',
];

function ReviewDetailPanel({ id, onClose }: { id: string | undefined; onClose: () => void }) {
  const { data: row } = useAiLeadQualificationQuery(id);
  const review = useReviewAiLeadQualification();
  const deleteRow = useDeleteAiLeadQualification();
  const toast = useToast();
  const [existingContactId, setExistingContactId] = useState('');
  const [reclassifyTo, setReclassifyTo] = useState<AiLeadClassification | ''>('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!id || !row) {
    return (
      <aside aria-label="AI qualification detail" className="sticky top-0 flex max-h-[calc(100vh-180px)] flex-col items-center justify-center rounded-card border border-border bg-surface p-6 text-center text-sm text-text-muted shadow-popover">
        Select an item to see its details.
      </aside>
    );
  }

  const decide = async (outcome: AiReviewOutcome, extra?: { contactId?: string; companyId?: string; classification?: AiLeadClassification }) => {
    try {
      await review.mutateAsync({ id: row.id, input: { outcome, ...extra } });
      toast.success('Updated.');
    } catch (err) {
      toast.error(err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Failed to update.');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteRow.mutateAsync(row.id);
      toast.success('Deleted.');
      setConfirmDelete(false);
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Failed to delete.');
    }
  };

  return (
    <aside aria-label="AI qualification detail" className="sticky top-0 flex max-h-[calc(100vh-180px)] flex-col overflow-hidden rounded-card border border-border bg-surface shadow-popover">
      <div className="flex flex-col gap-2 border-b border-border p-4">
        <div className="flex items-center gap-2">
          <span className={`inline-flex h-5.5 items-center rounded-[6px] px-2 text-xs font-semibold ${DECISION_LABEL[row.decision].className}`}>{DECISION_LABEL[row.decision].label}</span>
          <div className="flex-1" />
          <button type="button" aria-label="Close panel" onClick={onClose} className="flex size-7 items-center justify-center rounded-chip hover:bg-surface-subtle">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#454A54" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
        <h2 className="text-[17px] font-[650] leading-5.5 text-ink">{row.extractedContactName ?? row.senderName ?? row.senderEmail ?? 'Unknown sender'}</h2>
        <p className="truncate text-sm text-text-muted">{row.senderEmail}</p>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
        <dl className="grid grid-cols-[96px_1fr] items-start gap-x-2 gap-y-2.5 text-[13px] leading-4.5">
          <dt className="text-text-muted">Classification</dt>
          <dd><ClassificationPill classification={row.classification} /></dd>
          <dt className="text-text-muted">Priority</dt>
          <dd><PriorityBadge priority={row.priority} /></dd>
          <dt className="text-text-muted">Confidence</dt>
          <dd className="font-semibold">{row.confidence !== null ? `${Math.round(row.confidence * 100)}%` : '—'}</dd>
          <dt className="text-text-muted">Subject</dt>
          <dd className="wrap-break-word">{row.subject ?? '—'}</dd>
          <dt className="text-text-muted">Company</dt>
          <dd>{row.extractedCompanyName ?? '—'}</dd>
          <dt className="text-text-muted">Phone</dt>
          <dd>{row.extractedPhone ?? '—'}</dd>
          <dt className="text-text-muted">Model</dt>
          <dd>{row.modelUsed ?? '—'}</dd>
        </dl>

        <div className="h-px bg-divider" />

        {row.reason && (
          <div className="flex flex-col gap-1">
            <span className="text-[13px] font-semibold text-ink">AI reasoning</span>
            <p className="text-[13px] leading-5 text-text-secondary">{row.reason}</p>
          </div>
        )}
        {row.recommendedAction && (
          <div className="flex flex-col gap-1">
            <span className="text-[13px] font-semibold text-ink">Recommended action</span>
            <p className="text-[13px] leading-5 text-text-secondary">{row.recommendedAction}</p>
          </div>
        )}
        {row.errorMessage && (
          <div className="flex flex-col gap-1">
            <span className="text-[13px] font-semibold text-danger">Error</span>
            <p className="text-[13px] leading-5 text-text-secondary">{row.errorMessage}</p>
          </div>
        )}

        {row.reviewedAt && (
          <div className="rounded-control border border-border bg-surface-subtle p-2.5 text-[13px] text-text-secondary">
            Reviewed by {row.reviewedByName ?? 'someone'} · {row.reviewOutcome?.replaceAll('_', ' ')} — you can still move it below.
          </div>
        )}

        <div className="h-px bg-divider" />
        <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
          Existing contact ID (for "Mark existing customer")
          <input
            value={existingContactId}
            onChange={(e) => setExistingContactId(e.target.value)}
            placeholder="Paste a Contact ID"
            className="h-9 rounded-control border border-border-strong bg-surface px-2.5 text-sm font-normal"
          />
        </label>
        <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
          Reclassify as
          <div className="flex gap-2">
            <select
              value={reclassifyTo}
              onChange={(e) => setReclassifyTo(e.target.value as AiLeadClassification)}
              className="h-9 flex-1 rounded-control border border-border-strong bg-surface px-2.5 text-sm font-normal"
            >
              <option value="">Choose…</option>
              {RECLASSIFY_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {c.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
            <Button size="sm" variant="secondary" disabled={!reclassifyTo} loading={review.isPending} onClick={() => reclassifyTo && decide('reclassified', { classification: reclassifyTo })}>
              Apply
            </Button>
          </div>
        </label>
      </div>

      <div className="flex flex-wrap gap-2 border-t border-border bg-surface-subtle p-3">
        <Button size="sm" loading={review.isPending} onClick={() => decide('approved_lead')}>
          Approve as lead
        </Button>
        <Button size="sm" variant="secondary" loading={review.isPending} onClick={() => decide('converted_contact')}>
          Convert to contact
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={!existingContactId}
          loading={review.isPending}
          onClick={() => decide('marked_existing_customer', { contactId: existingContactId })}
        >
          Mark existing customer
        </Button>
        <Button size="sm" variant="secondary" loading={review.isPending} onClick={() => decide('rejected')}>
          Reject
        </Button>
        <Button size="sm" variant="danger" loading={review.isPending} onClick={() => decide('marked_spam')}>
          Mark as spam
        </Button>
        <Button size="sm" variant="danger" onClick={() => setConfirmDelete(true)}>
          Delete permanently
        </Button>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this item permanently?"
        description="This removes the audit row for good. Any Lead or Contact already created from it is not affected."
        confirmLabel="Delete"
        variant="danger"
        loading={deleteRow.isPending}
        onConfirm={handleDelete}
        onClose={() => setConfirmDelete(false)}
      />
    </aside>
  );
}

const LIST_TYPE_LABEL: Record<AiQualificationListType, { label: string; className: string }> = {
  blacklist: { label: 'Blacklist', className: 'bg-danger-soft text-danger' },
  whitelist: { label: 'Whitelist', className: 'bg-success-soft text-success' },
  classify: { label: 'Classify', className: 'bg-primary-soft text-primary-text' },
};

const MATCH_TYPE_LABEL: Record<AiQualificationMatchType, string> = {
  email: 'Email',
  domain: 'Domain',
  phone: 'Phone',
  keyword: 'Keyword',
};

const MATCH_TYPE_PLACEHOLDER: Record<AiQualificationMatchType, string> = {
  email: 'jane@example.com',
  domain: '@spamdomain.com',
  phone: '+15551234567 or 1555 (a prefix)',
  keyword: 'e.g. "unsubscribe" or "job application"',
};

/** The self-service rule book `AiQualificationService.checkRules` reads
 * before ever calling a provider. Blacklist/whitelist skip straight to
 * rejected/auto_create with no AI call made at all; classify doesn't skip
 * review — it labels the row and sends it to needs_review, for shaping
 * what AI Lead Review shows rather than fully automating the decision. */
function EmailRulesPanel() {
  const { data: rules, isLoading } = useAiQualificationEmailRulesQuery();
  const createRule = useCreateAiQualificationEmailRule();
  const deleteRule = useDeleteAiQualificationEmailRule();
  const toast = useToast();
  const [addOpen, setAddOpen] = useState(false);
  const [matchType, setMatchType] = useState<AiQualificationMatchType>('email');
  const [pattern, setPattern] = useState('');
  const [listType, setListType] = useState<AiQualificationListType>('blacklist');
  const [classification, setClassification] = useState<AiLeadClassification | ''>('');
  const [note, setNote] = useState('');

  const submit = async () => {
    try {
      await createRule.mutateAsync({
        matchType,
        pattern,
        listType,
        classification: listType === 'classify' ? (classification || undefined) : undefined,
        note: note.trim() || undefined,
      });
      toast.success('Rule added.');
      setAddOpen(false);
      setPattern('');
      setClassification('');
      setNote('');
    } catch (err) {
      toast.error(err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Failed to add rule.');
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteRule.mutateAsync(id);
      toast.success('Rule removed.');
    } catch (err) {
      toast.error(err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Failed to remove rule.');
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="max-w-2xl text-xs text-text-muted">
          Match on an email, domain, phone number, or a subject/body keyword. Blacklist/whitelist skip AI entirely — rejected or created
          instantly, no AI call made. Classify labels a match and sends it to Needs review instead of deciding on its own.
        </p>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          Add rule
        </Button>
      </div>

      {!isLoading && (rules?.length ?? 0) === 0 && <EmptyState title="No rules yet" description="Add a rule to bypass or shape AI qualification for matching leads." />}

      {rules && rules.length > 0 && (
        <div className="min-h-0 flex-1 overflow-y-auto rounded-card border border-border bg-surface shadow-card">
          {rules.map((rule) => (
            <div key={rule.id} className="flex h-11 items-center gap-3 border-b border-divider px-4 text-[13px] last:border-0">
              <span className={`inline-flex h-5.5 w-fit shrink-0 items-center rounded-[6px] px-2 text-xs font-semibold ${LIST_TYPE_LABEL[rule.listType].className}`}>
                {LIST_TYPE_LABEL[rule.listType].label}
                {rule.listType === 'classify' && rule.classification ? `: ${rule.classification.replaceAll('_', ' ')}` : ''}
              </span>
              <span className="w-16 shrink-0 text-xs text-text-muted">{MATCH_TYPE_LABEL[rule.matchType]}</span>
              <span className="min-w-0 flex-1 truncate font-mono text-ink">{rule.pattern}</span>
              {rule.note && <span className="min-w-0 flex-1 truncate text-text-muted">{rule.note}</span>}
              <span className="shrink-0 text-xs text-text-faint">{rule.createdByName ?? 'Unknown'}</span>
              <button
                type="button"
                aria-label={`Remove rule for ${rule.pattern}`}
                onClick={() => remove(rule.id)}
                className="flex size-7 shrink-0 items-center justify-center rounded-chip text-text-muted hover:bg-surface-subtle hover:text-danger"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog
        open={addOpen}
        title="Add a rule"
        onClose={() => setAddOpen(false)}
        actions={
          <Button size="sm" loading={createRule.isPending} disabled={!pattern.trim() || (listType === 'classify' && !classification)} onClick={submit}>
            Add
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
            Match on
            <select value={matchType} onChange={(e) => setMatchType(e.target.value as AiQualificationMatchType)} className="h-9 rounded-control border border-border-strong bg-surface px-2.5 text-sm font-normal">
              {(Object.entries(MATCH_TYPE_LABEL) as Array<[AiQualificationMatchType, string]>).map(([type, label]) => (
                <option key={type} value={type}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
            Pattern
            <input
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder={MATCH_TYPE_PLACEHOLDER[matchType]}
              className="h-9 rounded-control border border-border-strong bg-surface px-2.5 text-sm font-normal"
            />
          </label>
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
            Action
            <select value={listType} onChange={(e) => setListType(e.target.value as AiQualificationListType)} className="h-9 rounded-control border border-border-strong bg-surface px-2.5 text-sm font-normal">
              <option value="blacklist">Blacklist — always reject, never create a lead</option>
              <option value="whitelist">Whitelist — always create a lead, skip review</option>
              <option value="classify">Classify — label it and send to Needs review</option>
            </select>
          </label>
          {listType === 'classify' && (
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
              Classification
              <select value={classification} onChange={(e) => setClassification(e.target.value as AiLeadClassification)} className="h-9 rounded-control border border-border-strong bg-surface px-2.5 text-sm font-normal">
                <option value="">Choose…</option>
                {RECLASSIFY_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {c.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
            Note (optional)
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why this one's listed"
              className="h-9 rounded-control border border-border-strong bg-surface px-2.5 text-sm font-normal"
            />
          </label>
        </div>
      </Dialog>
    </div>
  );
}
